import { useMemo } from 'react';
import { Box, Button, Divider, Paper, Typography, Chip } from '@mui/material';
import MapIcon from '@mui/icons-material/Map';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import type { MessageEvent } from '@k-ucap/shared';
import { buildDeepLink } from '@/hooks/useDeepLinkContext.js';
import { useNavigate } from 'react-router-dom';

interface InspectorPanelProps {
  event: MessageEvent | null;
  sessionId: string | null;
}

export const InspectorPanel = ({ event, sessionId }: InspectorPanelProps) => {
  const navigate = useNavigate();

  const fieldEntries = useMemo(() => {
    if (!event) return [];
    const fields = (event.fields ?? event.payload ?? {}) as Record<string, unknown>;
    return Object.entries(fields);
  }, [event]);

  if (!event) {
    return (
      <Paper sx={{ p: 2, height: '100%', overflow: 'auto' }}>
        <Typography variant="subtitle2" gutterBottom>
          Inspector
        </Typography>
        <Typography variant="body2" color="text.secondary">
          메시지를 선택하면 상세 정보가 표시됩니다.
        </Typography>
      </Paper>
    );
  }

  const validation = event.validationStatus ?? event.validation ?? 'N/A';

  return (
    <Paper sx={{ p: 2, height: '100%', overflow: 'auto' }} data-testid="inspector-panel">
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="subtitle2">Inspector</Typography>
        <Chip
          label={validation}
          size="small"
          color={validation === 'FAIL' ? 'error' : validation === 'WARN' ? 'warning' : 'success'}
        />
      </Box>
      <Typography variant="body2" sx={{ fontFamily: 'monospace', mb: 0.5 }}>
        #{event.messageType} {event.name}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {new Date(event.timestamp).toLocaleString()}
      </Typography>
      <Divider sx={{ my: 1 }} />
      <Typography variant="caption" color="text.secondary">
        Fields ({fieldEntries.length})
      </Typography>
      <Box sx={{ mt: 0.5, maxHeight: 260, overflow: 'auto' }}>
        {fieldEntries.map(([key, value]) => (
          <Box key={key} sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
            <Typography variant="caption" color="text.secondary" noWrap>
              {key}
            </Typography>
            <Typography variant="caption" sx={{ fontFamily: 'monospace' }} noWrap>
              {typeof value === 'object' ? JSON.stringify(value) : String(value)}
            </Typography>
          </Box>
        ))}
      </Box>
      <Divider sx={{ my: 1 }} />
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <Button
          size="small"
          variant="outlined"
          startIcon={<AnalyticsIcon />}
          onClick={() =>
            navigate(
              buildDeepLink('/analyzer', {
                sessionId,
                runId: event.runId,
                messageId: event.id,
                timestamp: event.timestamp,
              })
            )
          }
        >
          Analyzer
        </Button>
        <Button
          size="small"
          variant="outlined"
          startIcon={<MapIcon />}
          onClick={() =>
            navigate(
              buildDeepLink('/gis', {
                sessionId,
                vehicleId: (event.fields?.vehicleId as string | undefined) ?? null,
                timestamp: event.timestamp,
              })
            )
          }
        >
          GIS
        </Button>
      </Box>
    </Paper>
  );
};
